import { type DayShape } from "@nav-games/nav-lights";

interface DayShapeFigureProps {
  shapes: readonly DayShape[];
  /// Optional side hoists for the dredger / underwater-operations
  /// signal (Rule 27(d)). Renderer puts these on side yardarms left
  /// (obstruction) and right (safe passage) of the main hoist.
  obstructionSide?: readonly DayShape[];
  safeSide?: readonly DayShape[];
  size?: number;
}

/// Renders a stack of black day shapes on a 100×100 viewbox. Each
/// shape occupies a fixed-height slot so multi-shape stacks
/// (NUC = ball+ball, RAM = ball+diamond+ball) hang at the right
/// vertical spacing. When `obstructionSide` / `safeSide` are
/// supplied, the canvas widens to fit two extra columns either side
/// of the main hoist (used only by the dredger signal so far).
/// Empty arrays render a placeholder "no shape" silhouette so the
/// catalogue is uniform in size.
export function DayShapeFigure({
  shapes,
  obstructionSide,
  safeSide,
  size = 80,
}: DayShapeFigureProps) {
  const slotH = 24;
  const slotW = 24;
  const slotGap = 8;

  const hasSides =
    (obstructionSide && obstructionSide.length > 0) ||
    (safeSide && safeSide.length > 0);

  // ViewBox is 100 wide for single-hoist arrangements; widens to
  // 160 when side hoists are present so the three columns + the
  // yardarm spreaders fit comfortably.
  const vbW = hasSides ? 160 : 100;
  const vbH = 100;
  const mainCx = vbW / 2;
  const obstructionCx = mainCx - 38;
  const safeCx = mainCx + 38;
  // Centre the main column vertically. Side columns hang from the
  // upper portion of the main mast (yardarm height around y=20).
  const mainStackHeight =
    Math.max(1, shapes.length) * slotH +
    (Math.max(1, shapes.length) - 1) * slotGap;
  const mainStartY = vbH / 2 - mainStackHeight / 2;
  const sideStartY = mainStartY + 6;

  // Render width/height: keep the SVG height equal to `size` but
  // scale the width to match the wider viewBox so the aspect ratio
  // stays correct.
  const renderW = Math.round((size * vbW) / vbH);
  const renderH = size;

  const ariaLabel = (() => {
    const main = shapes.join(" + ") || "no day shape";
    if (!hasSides) return main;
    const obstr =
      obstructionSide && obstructionSide.length
        ? `; obstruction side ${obstructionSide.join(" + ")}`
        : "";
    const safe =
      safeSide && safeSide.length
        ? `; safe side ${safeSide.join(" + ")}`
        : "";
    return main + obstr + safe;
  })();

  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      width={renderW}
      height={renderH}
      style={{ display: "block" }}
      aria-label={ariaLabel}
    >
      {/* Sky background — keeps shapes visible if the parent has a
          dark background. */}
      <rect x="0" y="0" width={vbW} height={vbH} fill="#f5f1e6" />

      {/* Main hoist — centred. */}
      <line
        x1={mainCx}
        y1="6"
        x2={mainCx}
        y2="94"
        stroke="#7d6a44"
        strokeWidth="1"
      />

      {/* Side yardarms — drawn only when side hoists are present.
          Reach out from the main mast at upper-mid height to the
          two side hoist lines. */}
      {hasSides && (
        <>
          <line
            x1={mainCx}
            y1={mainStartY}
            x2={obstructionCx}
            y2={mainStartY}
            stroke="#7d6a44"
            strokeWidth="1"
          />
          <line
            x1={mainCx}
            y1={mainStartY}
            x2={safeCx}
            y2={mainStartY}
            stroke="#7d6a44"
            strokeWidth="1"
          />
          {/* Vertical side hoists hanging from the yardarms. */}
          <line
            x1={obstructionCx}
            y1={mainStartY}
            x2={obstructionCx}
            y2={mainStartY + 60}
            stroke="#7d6a44"
            strokeWidth="1"
          />
          <line
            x1={safeCx}
            y1={mainStartY}
            x2={safeCx}
            y2={mainStartY + 60}
            stroke="#7d6a44"
            strokeWidth="1"
          />
        </>
      )}

      {shapes.length === 0 && !hasSides && (
        <text
          x={mainCx}
          y="55"
          textAnchor="middle"
          fontSize="11"
          fill="#7d6a44"
          fontFamily="ui-sans-serif, system-ui"
        >
          (no shape)
        </text>
      )}

      {/* Main shapes */}
      {shapes.map((shape, i) => {
        const cy = mainStartY + i * (slotH + slotGap) + slotH / 2;
        return (
          <DayShapeGlyph
            key={`m-${i}`}
            shape={shape}
            cx={mainCx}
            cy={cy}
            w={slotW}
            h={slotH}
          />
        );
      })}

      {/* Obstruction-side shapes (smaller scale so they read as
          auxiliary side markers, not co-equal with the main hoist) */}
      {obstructionSide?.map((shape, i) => {
        const cy = sideStartY + i * (slotH * 0.7 + 4) + (slotH * 0.7) / 2;
        return (
          <DayShapeGlyph
            key={`o-${i}`}
            shape={shape}
            cx={obstructionCx}
            cy={cy}
            w={slotW * 0.7}
            h={slotH * 0.7}
          />
        );
      })}

      {/* Safe-side shapes */}
      {safeSide?.map((shape, i) => {
        const cy = sideStartY + i * (slotH * 0.7 + 4) + (slotH * 0.7) / 2;
        return (
          <DayShapeGlyph
            key={`s-${i}`}
            shape={shape}
            cx={safeCx}
            cy={cy}
            w={slotW * 0.7}
            h={slotH * 0.7}
          />
        );
      })}

      {/* Side labels — only when side hoists are present. Annotate
          which side means "do not pass" vs "pass this side" so the
          reader doesn't have to remember the rule from memory. */}
      {hasSides && (
        <>
          <text
            x={obstructionCx}
            y={sideStartY + 60}
            textAnchor="middle"
            fontSize="6"
            fill="#7d6a44"
            fontFamily="ui-sans-serif, system-ui"
          >
            obstruction
          </text>
          <text
            x={safeCx}
            y={sideStartY + 60}
            textAnchor="middle"
            fontSize="6"
            fill="#7d6a44"
            fontFamily="ui-sans-serif, system-ui"
          >
            pass this side
          </text>
        </>
      )}
    </svg>
  );
}

function DayShapeGlyph({
  shape,
  cx,
  cy,
  w,
  h,
}: {
  shape: DayShape;
  cx: number;
  cy: number;
  w: number;
  h: number;
}) {
  const halfW = w / 2;
  const halfH = h / 2;
  switch (shape) {
    case "ball":
      return <circle cx={cx} cy={cy} r={Math.min(halfW, halfH)} fill="#000" />;
    case "diamond":
      return (
        <polygon
          points={`${cx},${cy - halfH} ${cx + halfW},${cy} ${cx},${cy + halfH} ${cx - halfW},${cy}`}
          fill="#000"
        />
      );
    case "two-cones-apex": {
      return (
        <>
          <polygon
            points={`${cx - halfW},${cy - halfH} ${cx + halfW},${cy - halfH} ${cx},${cy}`}
            fill="#000"
          />
          <polygon
            points={`${cx},${cy} ${cx + halfW},${cy + halfH} ${cx - halfW},${cy + halfH}`}
            fill="#000"
          />
        </>
      );
    }
    case "three-balls-triangle": {
      const r = Math.min(halfW, halfH) * 0.4;
      const topY = cy - halfH * 0.75;
      const botY = cy + halfH * 0.55;
      const sideX = halfW * 0.85;
      return (
        <>
          <circle cx={cx} cy={topY} r={r} fill="#000" />
          <circle cx={cx - sideX} cy={botY} r={r} fill="#000" />
          <circle cx={cx + sideX} cy={botY} r={r} fill="#000" />
        </>
      );
    }
    case "cone-up":
      return (
        <polygon
          points={`${cx},${cy - halfH} ${cx + halfW},${cy + halfH} ${cx - halfW},${cy + halfH}`}
          fill="#000"
        />
      );
    case "cone-down":
      return (
        <polygon
          points={`${cx - halfW},${cy - halfH} ${cx + halfW},${cy - halfH} ${cx},${cy + halfH}`}
          fill="#000"
        />
      );
    case "cylinder":
      return (
        <rect
          x={cx - halfW * 0.7}
          y={cy - halfH}
          width={halfW * 1.4}
          height={h}
          fill="#000"
        />
      );
  }
}
